/**
 * Main API handler for Vibeship DevTools
 * Handles dynamic routing and middleware pipeline
 */

import { NextRequest } from 'next/server';
import type { ApiConfig, HandlerOptions, RouteContext } from './types';
import { loadApiConfig } from './config-loader';
import { createRouter } from './router';
import { applyMiddleware } from './middleware';
import { createApiResponse, createErrorResponse } from './responses';

/**
 * Create a unified handler for all HTTP methods
 */
export function createHandler(options: HandlerOptions = {}) {
  const {
    basePath = '/api/vibeship',
    enableCors = true,
    enableHealthCheck = true,
  } = options;

  return async (request: NextRequest) => {
    // Generate request ID for tracking
    const requestId = generateRequestId();
    
    try {
      // Load configuration
      const config = options.config || await loadApiConfig();
      
      // Create route context
      const context: RouteContext = {
        config,
        request,
        requestId,
        basePath,
      };

      // Apply middleware pipeline
      const middlewareResult = await applyMiddleware(context, {
        cors: enableCors && config.security?.cors !== false,
        rateLimit: config.security?.rateLimit || false,
        authentication: config.security?.authentication || false,
      });

      if (middlewareResult) {
        // Middleware returned a response (e.g., CORS preflight, rate limit)
        return middlewareResult;
      }

      // Route the request
      const router = createRouter(config);
      const routeResult = await router.handle(context);

      if (routeResult) {
        return createApiResponse(routeResult, requestId);
      }

      // No route matched
      return createErrorResponse(
        'ROUTE_NOT_FOUND',
        404,
        {
          message: 'API endpoint not found',
          cause: `No handler found for ${request.method} ${getRoutePath(request, basePath)}`,
          solution: 'Check the API documentation for available endpoints',
          docs: 'https://vibeship.dev/docs/api',
        },
        requestId
      );

    } catch (error) {
      console.error('[VibeshipAPI] Unhandled error:', error);
      
      return createErrorResponse(
        'INTERNAL_ERROR',
        500,
        {
          message: 'An unexpected error occurred',
          cause: 'Internal server error',
          solution: 'Please try again. If the issue persists, check the logs or contact support',
          docs: 'https://vibeship.dev/docs/troubleshooting',
        },
        requestId,
        process.env.NODE_ENV === 'development' ? error : undefined
      );
    }
  };
}

/**
 * Generate a unique request ID for tracking
 */
function generateRequestId(): string {
  return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Extract the route path from the request
 */
function getRoutePath(request: NextRequest, basePath: string): string {
  const url = new URL(request.url);
  const fullPath = url.pathname;
  
  if (fullPath.startsWith(basePath)) {
    return fullPath.slice(basePath.length) || '/';
  }
  
  return fullPath;
}